import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { useUserStore } from "../../store/userStore";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useSupportChatStore } from "../../store/supportChatStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import ChatSidebar from "./components/ChatSidebar";
import ChatRoom from "./components/ChatRoom";
import EmptyChatState from "./components/EmptyChatState";

export default function SupportChatPage() {
  const { isDarkMode } = useTheme();
  const user = useUserStore((state) => state.user);
  const companyId = useCompaniesStore((state) => state.companyId);
  
  const getAuthToken = (): string | null => {
    return localStorage.getItem("token") || document.cookie
      .split("; ")
      .find((row) => row.startsWith("jwt="))
      ?.split("=")[1] || null;
  };

  const activeConversationId = useSupportChatStore((s) => s.activeConversationId);
  const setActiveConversation = useSupportChatStore((s) => s.setActiveConversation);
  const initializeChat = useSupportChatStore((s) => s.initializeChat);
  const reset = useSupportChatStore((s) => s.reset);

  const fetchGen = useSupportChatStore((s) => s._fetchGen);
  const isLoadingConversations = useSupportChatStore(
    (s) => s.isLoadingConversations,
  );
  const hasFetchedConversations = fetchGen > 0 && !isLoadingConversations;
  const conversations = useSupportChatStore((s) => s.conversations);
  const activeTab = useSupportChatStore((s) => s.activeTab);
  const setActiveTab = useSupportChatStore((s) => s.setActiveTab);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedChatId = searchParams.get("chat");

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(true);

  useEffect(() => {
    if (!companyId || !user?.id) return;
    
    const token = getAuthToken();
    if (!token) return;

    initializeChat(token, user.id, companyId);

    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, companyId]);

  useEffect(() => {
    if (!requestedChatId) return;
    if (!hasFetchedConversations) return;
    if (requestedChatId === activeConversationId) return;
    setActiveConversation(requestedChatId);
    setIsMobileSidebarOpen(false);
  }, [
    requestedChatId,
    activeConversationId,
    setActiveConversation,
    hasFetchedConversations,
  ]);

  const syncedTabForConvRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeConversationId) {
      syncedTabForConvRef.current = null;
      return;
    }
    if (syncedTabForConvRef.current === activeConversationId) return;
    const conv = conversations.find((c) => c.id === activeConversationId);
    if (!conv) return;
    syncedTabForConvRef.current = activeConversationId;
    const desired: typeof activeTab = conv.trip_id ? "viajes" : "chats";
    if (activeTab !== desired) {
      setActiveTab(desired);
    }
  }, [activeConversationId, conversations, activeTab, setActiveTab]);

  const handleSelectConversation = (conversationId: string) => {
    navigate(`/support?chat=${encodeURIComponent(conversationId)}`, {
      replace: true,
    });
    setIsMobileSidebarOpen(false);
  };

  const handleBackToList = () => {
    setSearchParams({}, { replace: true });
    setActiveConversation(null);
    setIsMobileSidebarOpen(true);
  };

  const { t } = useTranslation("support");

  if (!companyId) {
    return (
      <div className={`h-full flex items-center justify-center ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
        <div className="flex flex-col items-center gap-4 max-w-sm text-center px-6">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            isDarkMode ? "bg-dark-hover" : "bg-gray-100"
          }`}>
            <FontAwesomeIcon
              icon={faBuilding}
              className={`text-2xl ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          </div>
          <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {t("chat.selectCompanyTitle")}
          </h2>
          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {t("chat.selectCompanyDescription")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full min-h-0 flex ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
      <div
        className={`
          ${isMobileSidebarOpen ? "flex" : "hidden"}
          md:flex flex-col min-h-0 w-full md:w-80 lg:w-96 border-r
          ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"}
        `}
      >
        <ChatSidebar onSelectConversation={handleSelectConversation} />
      </div>

      <div
        className={`
          ${!isMobileSidebarOpen ? "flex" : "hidden"}
          md:flex flex-1 flex-col min-h-0 min-w-0
        `}
      >
        {activeConversationId ? (
          <ChatRoom
            conversationId={activeConversationId}
            onBack={handleBackToList}
          />
        ) : (
          <EmptyChatState />
        )}
      </div>
    </div>
  );
}
